import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Bell, CheckCircle2, XCircle, ShoppingBag, BellOff, FilePlus, Stethoscope, Search, Target } from "lucide-react";

const TYPE_META = {
  approved:        { icon: CheckCircle2, color: "text-emerald-600 bg-emerald-100" },
  rejected:        { icon: XCircle,      color: "text-rose-600 bg-rose-100" },
  sold:            { icon: ShoppingBag,  color: "text-blue-600 bg-blue-100" },
  new_listing:     { icon: FilePlus,     color: "text-blue-700 bg-blue-100" },
  new_service:     { icon: Stethoscope,  color: "text-indigo-600 bg-indigo-100" },
  new_med_request: { icon: Search,       color: "text-amber-700 bg-amber-100" },
  match_request:   { icon: Target,       color: "text-emerald-700 bg-emerald-100" },
};

function timeAgo(iso) {
  const d = new Date(iso);
  const sec = Math.round((Date.now() - d.getTime()) / 1000);
  if (sec < 60) return "hace un momento";
  const min = Math.round(sec / 60);
  if (min < 60) return `hace ${min} min`;
  const h = Math.round(min / 60);
  if (h < 24) return `hace ${h} h`;
  const days = Math.round(h / 24);
  if (days < 7) return `hace ${days} día(s)`;
  return d.toLocaleDateString("es-MX");
}

export default function NotificationsBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch {}
  }, []);

  useEffect(() => {
    fetchAll();
    const t = setInterval(fetchAll, 30000);
    return () => clearInterval(t);
  }, [fetchAll]);

  const onOpenChange = async (v) => {
    setOpen(v);
    if (v && unread > 0) {
      try {
        await api.post("/notifications/read-all");
        // Optimistically update
        setItems((prev) => prev.map((n) => ({ ...n, read: true })));
        setUnread(0);
      } catch {}
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>
        <button
          className="relative h-11 w-11 grid place-items-center rounded-lg hover:bg-blue-50 text-slate-700 transition-colors"
          aria-label="Notificaciones"
          data-testid="notifications-bell"
        >
          <Bell className="w-5 h-5" />
          {unread > 0 && (
            <span className="absolute top-1.5 right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-white" data-testid="notifications-badge">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0 max-h-[70vh] overflow-hidden flex flex-col" data-testid="notifications-dropdown">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="font-display font-semibold text-slate-900">Notificaciones</div>
            <div className="text-xs text-slate-500">{unread > 0 ? `${unread} sin leer` : "Todo al día"}</div>
          </div>
        </div>
        <div className="overflow-y-auto flex-1">
          {items.length === 0 ? (
            <div className="p-6 text-center text-sm text-slate-500" data-testid="notifications-empty">
              <BellOff className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              Sin notificaciones por ahora.
            </div>
          ) : (
            items.map((n) => {
              const meta = TYPE_META[n.type] || TYPE_META.approved;
              const Icon = meta.icon;
              const linkTo = n.type === "new_service"
                ? "/admin/servicios"
                : n.type === "new_listing"
                ? "/admin"
                : n.type === "new_med_request"
                ? "/busquedas"
                : n.listing_id ? `/medicamento/${n.listing_id}` : "/";
              return (
                <Link
                  key={n.id}
                  to={linkTo}
                  onClick={() => setOpen(false)}
                  className={`flex gap-3 px-4 py-3 border-b border-slate-100 hover:bg-blue-50 transition-colors ${!n.read ? "bg-blue-50/40" : ""}`}
                  data-testid={`notification-item-${n.id}`}
                >
                  <div className={`h-9 w-9 rounded-xl ${meta.color} grid place-items-center shrink-0`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-900 truncate">{n.title}</div>
                    <div className="text-xs text-slate-500 truncate">{n.body}</div>
                    <div className="text-[11px] text-slate-400 mt-0.5">{timeAgo(n.created_at)}</div>
                  </div>
                  {!n.read && <span className="w-2 h-2 rounded-full bg-blue-600 mt-2 shrink-0" />}
                </Link>
              );
            })
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
